/** Re-authenticate in place on a 401 mid-session (spec §10.3), so the page and unsaved form state survive. */
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiProblem, api, apiEvents, unwrap } from '@/lib/api/client'
import { SESSION_KEY, type Session } from '@/lib/auth'
import { t } from '@/lib/text'

export function SessionExpiredDialog() {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [needsCode, setNeedsCode] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(
    () =>
      apiEvents.onUnauthorized(() => {
        const session = queryClient.getQueryData<Session>(SESSION_KEY)
        if (session?.state === 'ready') setEmail(session.me.email)
      }),
    [queryClient],
  )

  function reset() {
    setEmail(null)
    setPassword('')
    setCode('')
    setNeedsCode(false)
    setError(null)
  }

  async function submit() {
    if (!email) return
    setBusy(true)
    setError(null)
    try {
      await api.GET('/api/v1/auth/csrf/')
      let next: string | null | undefined
      if (!needsCode) {
        next = unwrap(await api.POST('/api/v1/auth/login/', { body: { email, password } })).next_step
      } else {
        next = unwrap(await api.POST('/api/v1/auth/totp/verify/', { body: { code } })).next_step
      }
      if (next === 'totp') {
        setNeedsCode(true)
        return
      }
      if (next) {
        window.location.assign('/')
        return
      }
      reset()
      await queryClient.invalidateQueries()
    } catch (caught) {
      const problem = caught instanceof ApiProblem ? caught : null
      setError(problem?.fieldErrors.code?.[0] ?? problem?.fieldErrors.password?.[0] ?? problem?.detail ?? t('core.common.error_body'))
    } finally {
      setBusy(false)
    }
  }

  const fieldId = needsCode ? 'session-code' : 'session-password'
  return (
    <Dialog open={email !== null} onOpenChange={() => {}}>
      <DialogContent
        closeLabel={t('core.shell.close')}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        className="[&>button:last-child]:hidden"
      >
        <DialogHeader>
          <DialogTitle>{t('core.session.title')}</DialogTitle>
          <DialogDescription>{t('core.session.body')}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            void submit()
          }}
        >
          <p className="text-sm font-medium">{email}</p>
          <Label htmlFor={fieldId}>{needsCode ? t('core.session.code') : t('core.session.password')}</Label>
          {needsCode ? (
            <Input
              id="session-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              spellCheck={false}
              aria-invalid={!!error}
              aria-describedby={error ? 'session-error' : undefined}
              required
            />
          ) : (
            <Input
              id="session-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              aria-invalid={!!error}
              aria-describedby={error ? 'session-error' : undefined}
              required
            />
          )}
          {error && (
            <p id="session-error" className="text-sm text-destructive" aria-live="polite">
              {error}
            </p>
          )}
          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => window.location.assign('/login')}>
              {t('core.session.elsewhere')}
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? t('core.session.working') : t('core.session.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
